import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Image } from "@/components/ui/image";
import { queries } from "@/utils/query";
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useMatches,
} from "@tanstack/react-router";
import {
  BadgeIcon,
  BanIcon,
  HomeIcon,
  IdCardIcon,
  MailIcon,
  ShieldIcon,
  UserRoundIcon,
} from "lucide-react";
import { useMemo } from "react";

export const Route = createFileRoute("/user/settings")({
  beforeLoad: ({ context }) => {
    if (!context.auth.session) {
      throw redirect({
        to: "/auth/login",
      });
    }
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      queries.auth.getCurrentSession.getFetchOptions(),
    ),
  component: RouteComponent,
});

const sidebarNavItems = [
  {
    label: "首页",
    to: "/user/settings",
    icon: HomeIcon,
  },
  {
    label: "个人资料",
    to: "/user/settings/profile",
    icon: UserRoundIcon,
  },
  {
    label: "勋章",
    to: "/user/settings/medals",
    icon: BadgeIcon,
  },
  {
    label: "账号安全",
    to: "/user/settings/security",
    icon: ShieldIcon,
  },
  {
    label: "隐私设置",
    to: "/user/settings/privacy",
    icon: MailIcon,
  },
  {
    label: "黑名单管理",
    to: "/user/settings/blacklist",
    icon: BanIcon,
  },
  {
    label: "实名认证",
    to: "/user/settings/real-name",
    icon: IdCardIcon,
  },
] as const;

function RouteComponent() {
  const { data: currentUser } = queries.auth.getCurrentSession.useSuspenseQuery(
    {
      select: (data) => {
        if (data.session) {
          return data.user;
        }
        throw new Error("Session not found");
      },
    },
  );

  const matches = useMatches();
  const currentPageTitle = useMemo(
    () => matches.at(-1)?.meta?.at(-1)?.title,
    [matches],
  );

  return (
    <div className="container mx-auto">
      <div className="flex gap-4">
        <div className="w-56 space-y-2">
          <Card>
            <CardContent className="pt-6 px-4 text-center">
              <Image
                alt=""
                src={currentUser.avatarUrl}
                className="size-24 bg-gray-solid mx-auto"
                width={96}
                aspectRatio={1}
              />

              <h3 className="mt-4">{currentUser.nickname}</h3>

              <div className="mt-4 space-y-2">
                <Button intent="soft" flavor="accent" className="w-full">
                  发布作品
                </Button>
                <Button intent="outline" flavor="gray" className="w-full">
                  我的草稿
                </Button>
              </div>
            </CardContent>
          </Card>

          <nav className="bg-gray-subtle-bg text-gray-fg-secondary">
            <ul>
              {sidebarNavItems.map((item) => (
                <li key={item.to} className="py-3.5">
                  <Link
                    //   TODO: remove type cast after all routes added
                    to={item.to as unknown as string}
                    className="group flex gap-x-4 px-4 py-0.5 items-center"
                    activeProps={{
                      className:
                        "text-accent-fg-primary border-l-2 border-accent-border",
                    }}
                    activeOptions={{ exact: true }}
                  >
                    <item.icon className="size-5 shrink-0" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="flex-1">
          <div className="bg-gray-subtle-bg px-6 py-3 border-l-2 border-accent-border">
            <h1>{currentPageTitle}</h1>
          </div>

          <div className="mt-2 space-y-2 *:bg-gray-subtle-bg *:p-6">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
